// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import FormDialog from '../../components/Dialog/FormDialog';
import TextInput from '../../components/Form/TextInput';
import { fmt } from '../../utils/common';
import { DialogProps } from './DialogProps';

export const ModifyCommercialAgreementDialog: React.FC<
  DialogProps & {
    currFeeReceiver: string;
    currUtilityFees: string;
    currBillingPeriodMinutes?: string;
    currRate?: string;
    onModify:
      | ((
          feeReceiver: string,
          utilityFees: string,
          currBillingPeriodMinutes?: string,
          currRate?: string,
        ) => Promise<unknown>)
      | undefined;
  }
> = ({
  open,
  onClose,
  onModify,
  currFeeReceiver,
  currUtilityFees,
  currBillingPeriodMinutes,
  currRate,
}) => {
  const [feeReceiver, setFeeReceiver] = React.useState(currFeeReceiver);
  const [utilityFees, setUtilityFees] = React.useState(fmt(currUtilityFees, 2));
  const [billingPeriodMinutes, setBillingPeriodMinutes] = React.useState(
    currBillingPeriodMinutes || '',
  );
  const [rate, setRatePerDay] = React.useState(currRate ? fmt(currRate, 2) : '');
  const handleClose = React.useCallback(() => {
    setFeeReceiver('');
    setUtilityFees('');
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(() => {
    if (onModify) {
      return onModify(feeReceiver, utilityFees, billingPeriodMinutes, rate).finally(() => {
        handleClose();
      });
    }
    handleClose();
    return Promise.resolve();
  }, [handleClose, onModify, feeReceiver, utilityFees, billingPeriodMinutes, rate]);
  return (
    <FormDialog
      open={open}
      title="Modify Commercial Agreement"
      onCancel={onClose}
      onSubmit={handleSubmit}
      submitName="Modify"
    >
      <TextInput
        enableTopLabel
        required
        label="Fee Receiver"
        value={feeReceiver}
        setValue={setFeeReceiver}
      />
      <TextInput
        required
        enableTopLabel
        label="Credential Billing Fee (USD)"
        value={utilityFees}
        setValue={setUtilityFees}
      />
      <TextInput
        required={billingPeriodMinutes !== ''}
        enableTopLabel
        label="Fee per day (USD)"
        value={rate || ''}
        setValue={setRatePerDay}
      />
      <TextInput
        enableTopLabel
        required={rate !== ''}
        label="Billing Period (minutes)"
        value={billingPeriodMinutes || ''}
        setValue={setBillingPeriodMinutes}
      />
    </FormDialog>
  );
};
